import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Badge,
  Divider,
  Skeleton,
  Stack,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Tune as TuneIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

// Simple in-memory cache for text field values (brand etc.)
const fieldValuesCache = new Map();
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min

function getCachedValues(key) {
  const entry = fieldValuesCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) { fieldValuesCache.delete(key); return null; }
  return entry.data;
}
function setCachedValues(key, data) {
  fieldValuesCache.set(key, { data, ts: Date.now() });
}

/**
 * ListingDetailsFilter — Filtres structurés par champs de détails (JSONB)
 *
 * Design moderne, cohérent avec les autres sections de AdvancedFiltersStructured.
 * Clés émises vers le parent (avec préfixe details_ déjà inclus) :
 *   - Numérique : details_<key>_min, details_<key>_max
 *   - Boolean   : details_<key> = true
 *   - Checkbox  : details_<key> = ['val1', 'val2']
 *   - Select    : details_<key> = 'val'
 */
const ListingDetailsFilter = ({
  offerType,
  value = {},
  onChange,
  isExpanded = false,
  onToggleExpand
}) => {
  const { t } = useTranslation(['common', 'listingFields']);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  // Validation errors for numeric range fields: { [def.key]: string | null }
  const [rangeErrors, setRangeErrors] = useState({});
  // Cached distinct values for text-autocomplete fields: { [def.key]: string[] }
  const [textFieldValues, setTextFieldValues] = useState({});
  const [textFieldInputs, setTextFieldInputs] = useState({});
  const [loadingTextValues, setLoadingTextValues] = useState({});

  // ─── Fetch definitions ────────────────────────────────────────────────────
  const fetchDefinitions = useCallback(async (type) => {
    if (!type) { setFieldDefs([]); return; }
    setLoading(true);
    try {
      const res = await api.get('/listing-field-definitions', { params: { offer_type: type } });
      if (res.data?.success) {
        const sorted = (res.data.data || [])
          .filter((d) => d.is_searchable)
          .sort((a, b) => a.display_order - b.display_order);
        setFieldDefs(sorted);
      } else {
        setFieldDefs([]);
      }
    } catch {
      setFieldDefs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDefinitions(offerType);
  }, [offerType, fetchDefinitions]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const tOpt = (opt) => t(`listingFields:opt_${opt}`, opt);
  const tLabel = (def) => {
    const label = t(`listingFields:${def.key}`, def.label);
    return def.icon ? `${def.icon} ${label}` : label;
  };
  const parseOpts = (def) =>
    Array.isArray(def.options) ? def.options : JSON.parse(def.options || '[]');

  const getWidget = (def) => {
    if (def.search_widget) return def.search_widget;
    const opts = parseOpts(def);
    if (def.field_type === 'number') return 'range';
    if (def.field_type === 'boolean') return 'toggle';
    if (def.field_type === 'text') return 'text-autocomplete';
    if (opts.length > 4) return 'select';
    return 'checkbox';
  };

  // ─── Fetch distinct values for text fields (brand, etc.) ─────────────────
  const fetchTextValues = useCallback(async (def, { force = false } = {}) => {
    const cacheKey = `${offerType}_${def.key}`;
    const cached = force ? null : getCachedValues(cacheKey);
    if (Array.isArray(cached) && cached.length > 0) {
      setTextFieldValues(prev => ({ ...prev, [def.key]: cached }));
      return;
    }
    setLoadingTextValues(prev => ({ ...prev, [def.key]: true }));
    try {
      const res = await api.get('/listing-field-definitions/values', {
        params: { key: def.key, offer_type: offerType }
      });
      if (res.data?.success) {
        const vals = Array.from(
          new Set((res.data.data || []).map((entry) => String(entry).trim()).filter(Boolean))
        );
        if (vals.length > 0) {
          setCachedValues(cacheKey, vals);
        } else {
          fieldValuesCache.delete(cacheKey);
        }
        setTextFieldValues(prev => ({ ...prev, [def.key]: vals }));
      }
    } catch {
      // Silently fail — field stays with empty options list
    } finally {
      setLoadingTextValues(prev => ({ ...prev, [def.key]: false }));
    }
  }, [offerType]);

  // ─── Validation helper for numeric range fields ───────────────────────────
  const validateRange = useCallback((defKey, minVal, maxVal) => {
    if (minVal !== undefined && minVal !== null && minVal !== '') {
      if (Number(minVal) < 0) {
        return t('common:search.details.errors.negative', 'La valeur ne peut pas être négative');
      }
    }
    if (maxVal !== undefined && maxVal !== null && maxVal !== '') {
      if (Number(maxVal) < 0) {
        return t('common:search.details.errors.negative', 'La valeur ne peut pas être négative');
      }
    }
    if (
      minVal !== undefined && minVal !== null && minVal !== '' &&
      maxVal !== undefined && maxVal !== null && maxVal !== '' &&
      Number(minVal) > Number(maxVal)
    ) {
      return t('common:search.details.errors.minMaxInvalid', 'Le minimum doit être ≤ au maximum');
    }
    return null;
  }, [t]);

  const handleChange = useCallback(
    (patch) => onChange({ ...value, ...patch }),
    [value, onChange]
  );
  const handleClearKey = useCallback(
    (...keys) => {
      const next = { ...value };
      keys.forEach((k) => delete next[k]);
      onChange(next);
    },
    [value, onChange]
  );

  const handleRangeChange = useCallback((minKey, maxKey, defKey, isMin, rawVal) => {
    const targetKey = isMin ? minKey : maxKey;
    if (rawVal === '') {
      handleClearKey(targetKey);
      // Re-validate remaining value
      const otherVal = isMin ? value[maxKey] : value[minKey];
      const err = isMin ? validateRange(defKey, undefined, otherVal) : validateRange(defKey, otherVal, undefined);
      setRangeErrors(prev => ({ ...prev, [defKey]: err }));
      return;
    }
    const num = Number(rawVal);
    if (isNaN(num)) return;
    const newMin = isMin ? num : value[minKey];
    const newMax = isMin ? value[maxKey] : num;
    const err = validateRange(defKey, newMin, newMax);
    setRangeErrors(prev => ({ ...prev, [defKey]: err }));
    if (!err) {
      handleChange({ [targetKey]: num });
    }
  }, [value, handleChange, handleClearKey, validateRange]);

  // ─── Group fields by widget type ──────────────────────────────────────────
  const rangeDefs = useMemo(() => fieldDefs.filter((d) => getWidget(d) === 'range'), [fieldDefs]);
  const checkboxDefs = useMemo(() => fieldDefs.filter((d) => getWidget(d) === 'checkbox'), [fieldDefs]);
  const selectDefs = useMemo(() => fieldDefs.filter((d) => getWidget(d) === 'select'), [fieldDefs]);
  const toggleDefs = useMemo(() => fieldDefs.filter((d) => getWidget(d) === 'toggle'), [fieldDefs]);
  const textDefs = useMemo(() => fieldDefs.filter((d) => getWidget(d) === 'text-autocomplete'), [fieldDefs]);

  useEffect(() => {
    setTextFieldInputs((prev) => {
      let changed = false;
      const next = { ...prev };

      textDefs.forEach((def) => {
        const nextValue = value[`details_${def.key}`] || '';
        if (next[def.key] !== nextValue) {
          next[def.key] = nextValue;
          changed = true;
        }
      });

      Object.keys(next).forEach((key) => {
        if (!textDefs.some((def) => def.key === key)) {
          delete next[key];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [textDefs, value]);

  // Fetch text-field distinct values when definitions load
  useEffect(() => {
    textDefs.forEach((def) => {
      if (!Array.isArray(textFieldValues[def.key]) && !loadingTextValues[def.key]) {
        fetchTextValues(def);
      }
    });
  }, [textDefs]); // intentionally omit fetchTextValues / textFieldValues to avoid re-fetching on state change

  // ─── Active count (per distinct field key, not per min/max pair) ──────────
  const activeCount = useMemo(() => {
    const activeFields = new Set();
    Object.entries(value).forEach(([key, val]) => {
      if (
        val === undefined ||
        val === null ||
        val === '' ||
        (Array.isArray(val) && val.length === 0)
      ) return;
      const base = key.replace(/^details_/, '').replace(/_(min|max)$/, '');
      activeFields.add(base);
    });
    return activeFields.size;
  }, [value]);

  if (!offerType || (fieldDefs.length === 0 && !loading)) return null;

  // ─── Shared chip styles ───────────────────────────────────────────────────
  const activeChipSx = (active) => ({
    cursor: 'pointer',
    fontWeight: active ? 700 : 400,
    bgcolor: active ? 'primary.main' : 'action.hover',
    color: active ? 'white' : 'text.primary',
    border: active ? 'none' : '1px solid',
    borderColor: 'divider',
    transition: 'all 0.15s ease',
    '&:hover': { bgcolor: active ? 'primary.dark' : 'action.selected' }
  });

  const toggleChipSx = (active) => ({
    cursor: 'pointer',
    fontWeight: active ? 700 : 400,
    bgcolor: active ? 'success.main' : 'action.hover',
    color: active ? 'white' : 'text.primary',
    border: active ? 'none' : '1px solid',
    borderColor: 'divider',
    transition: 'all 0.15s ease',
    '&:hover': { bgcolor: active ? 'success.dark' : 'action.selected' }
  });

  const subHeadingSx = {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'text.secondary',
    fontSize: '0.68rem',
    mb: 1.5,
    display: 'block'
  };

  const clearBtnSx = {
    height: 18,
    fontSize: '0.65rem',
    cursor: 'pointer',
    bgcolor: 'error.light',
    color: 'white',
    '&:hover': { bgcolor: 'error.main' }
  };

  return (
    <Box>
      {/* ── Section header (matches Location / Price header style) ────────── */}
      <Box
        onClick={onToggleExpand}
        sx={{
          p: 2,
          cursor: 'pointer',
          background: isExpanded ? 'var(--filter-primary-gradient)' : 'transparent',
          color: isExpanded ? 'white' : 'inherit',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          '&:hover': {
            background: isExpanded
              ? 'var(--filter-primary-gradient)'
              : 'rgba(79, 172, 254, 0.1)',
            transform: 'translateY(-1px)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TuneIcon sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('common:search.details.title', 'Caractéristiques')}
          </Typography>
          <Badge badgeContent={activeCount} color="secondary" sx={{ ml: 2 }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeCount > 0 && (
            <Chip
              label={t('common:search.details.activeCount', {
                count: activeCount,
                defaultValue: '{{count}} actif(s)'
              })}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onChange({});
              }}
              sx={{
                bgcolor: 'rgba(255,255,255,0.25)',
                color: isExpanded ? 'white' : 'text.primary',
                fontWeight: 600
              }}
            />
          )}
          <ExpandMoreIcon
            sx={{
              transition: 'transform 0.3s ease',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </Box>
      </Box>

      {/* ── Collapsible content ────────────────────────────────────────────── */}
      <Collapse in={isExpanded} timeout={300}>
        <Box sx={{ p: 3 }}>
          {loading ? (
            <Stack spacing={2}>
              {[...Array(3)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={40}
                  sx={{ borderRadius: 1 }}
                />
              ))}
            </Stack>
          ) : (
            <>
              {/* ─── Numeric / Range fields ─────────────────────────────── */}
              {rangeDefs.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={subHeadingSx}>
                    {t('common:search.details.numeric', 'Valeurs numériques')}
                  </Typography>
                  <Grid container spacing={2}>
                    {rangeDefs.map((def) => {
                      const minKey = `details_${def.key}_min`;
                      const maxKey = `details_${def.key}_max`;
                      const hasValue =
                        value[minKey] !== undefined || value[maxKey] !== undefined;
                      const fieldError = rangeErrors[def.key];
                      return (
                        <Grid item xs={12} key={def.key}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={fieldError ? 'error.main' : hasValue ? 'primary.main' : 'text.primary'}
                            >
                              {tLabel(def)}
                            </Typography>
                            {def.unit && (
                              <Chip
                                label={def.unit}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'action.hover' }}
                              />
                            )}
                            {hasValue && (
                              <Chip
                                label={t('common:filters.clear', 'Effacer')}
                                size="small"
                                onClick={() => {
                                  handleClearKey(minKey, maxKey);
                                  setRangeErrors(prev => { const n = { ...prev }; delete n[def.key]; return n; });
                                }}
                                sx={clearBtnSx}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex' }}>
                              <TextField
                                size="small"
                                type="number"
                                placeholder={t('common:search.details.min', 'Min')}
                                value={value[minKey] ?? ''}
                                error={!!fieldError}
                                inputProps={{ min: 0 }}
                                onChange={(e) =>
                                  handleRangeChange(minKey, maxKey, def.key, true, e.target.value)
                                }
                                InputProps={
                                  def.unit
                                    ? {
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <Typography variant="caption" color="text.secondary">
                                              {def.unit}
                                            </Typography>
                                          </InputAdornment>
                                        )
                                      }
                                    : undefined
                                }
                                sx={{
                                  flex: 1,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px 0 0 8px',
                                    '& fieldset': { borderRight: 'none' }
                                  }
                                }}
                              />
                              <TextField
                                size="small"
                                type="number"
                                placeholder={t('common:search.details.max', 'Max')}
                                value={value[maxKey] ?? ''}
                                error={!!fieldError}
                                inputProps={{ min: 0 }}
                                onChange={(e) =>
                                  handleRangeChange(minKey, maxKey, def.key, false, e.target.value)
                                }
                                InputProps={
                                  def.unit
                                    ? {
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <Typography variant="caption" color="text.secondary">
                                              {def.unit}
                                            </Typography>
                                          </InputAdornment>
                                        )
                                      }
                                    : undefined
                                }
                                sx={{
                                  flex: 1,
                                  '& .MuiOutlinedInput-root': { borderRadius: '0 8px 8px 0' }
                                }}
                              />
                            </Box>
                            {fieldError && (
                              <Typography variant="caption" color="error">
                                {fieldError}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* Divider between groups */}
              {rangeDefs.length > 0 && (textDefs.length > 0 || checkboxDefs.length > 0 || selectDefs.length > 0) && (
                <Divider sx={{ my: 2 }} />
              )}

              {/* ─── Text-autocomplete fields (brand / marque, etc.) ─────── */}
              {textDefs.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={subHeadingSx}>
                    {t('common:search.details.textFields', 'Recherche par valeur')}
                  </Typography>
                  {textDefs.map((def) => {
                    const singleKey = `details_${def.key}`;
                    const hasValue = !!value[singleKey];
                    const options = textFieldValues[def.key] || [];
                    const inputValue = textFieldInputs[def.key] ?? value[singleKey] ?? '';
                    const isLoadingOpts = !!loadingTextValues[def.key];
                    return (
                      <Box key={def.key} sx={{ mb: 2 }}>
                        <Autocomplete
                          freeSolo
                          openOnFocus
                          autoHighlight
                          selectOnFocus
                          clearOnBlur={false}
                          options={options}
                          loading={isLoadingOpts}
                          inputValue={inputValue}
                          value={value[singleKey] || null}
                          filterOptions={(availableOptions, state) => {
                            const needle = state.inputValue.trim().toLowerCase();
                            if (!needle) return availableOptions.slice(0, 50);
                            return availableOptions
                              .filter((option) => option.toLowerCase().includes(needle))
                              .slice(0, 50);
                          }}
                          onOpen={() => {
                            if (!options.length && !isLoadingOpts) {
                              fetchTextValues(def, { force: true });
                            }
                          }}
                          onChange={(_, newValue) => {
                            const nextValue = typeof newValue === 'string' ? newValue.trim() : '';
                            setTextFieldInputs((prev) => ({ ...prev, [def.key]: nextValue }));
                            if (!nextValue) handleClearKey(singleKey);
                            else handleChange({ [singleKey]: nextValue });
                          }}
                          onInputChange={(_, newInput, reason) => {
                            if (reason === 'input') {
                              setTextFieldInputs((prev) => ({ ...prev, [def.key]: newInput }));
                              const nextValue = newInput.trim();
                              if (nextValue) {
                                handleChange({ [singleKey]: nextValue });
                              } else {
                                handleClearKey(singleKey);
                              }
                            } else if (reason === 'clear') {
                              setTextFieldInputs((prev) => ({ ...prev, [def.key]: '' }));
                              handleClearKey(singleKey);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label={tLabel(def)}
                              placeholder={t('common:search.details.typeToSearch', 'Saisissez ou sélectionnez...')}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isLoadingOpts && <CircularProgress size={14} sx={{ mr: 1 }} />}
                                    {params.InputProps.endAdornment}
                                  </>
                                )
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  borderColor: hasValue ? 'primary.main' : undefined,
                                  borderWidth: hasValue ? 2 : 1
                                }
                              }}
                            />
                          )}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Divider between groups */}
              {(rangeDefs.length > 0 || textDefs.length > 0) && (checkboxDefs.length > 0 || selectDefs.length > 0) && (
                <Divider sx={{ my: 2 }} />
              )}

              {/* ─── Checkbox + Select fields (option-based) ─────────────── */}
              {(checkboxDefs.length > 0 || selectDefs.length > 0) && (
                <Box sx={{ mb: toggleDefs.length > 0 ? 3 : 0 }}>
                  <Typography sx={subHeadingSx}>
                    {t('common:search.details.options', 'Options')}
                  </Typography>

                  {/* Checkbox → multi-select chips */}
                  {checkboxDefs.map((def) => {
                    const singleKey = `details_${def.key}`;
                    const opts = parseOpts(def);
                    const currentVals = Array.isArray(value[singleKey]) ? value[singleKey] : [];
                    const hasValue = currentVals.length > 0;
                    return (
                      <Box key={def.key} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={hasValue ? 'primary.main' : 'text.primary'}
                          >
                            {tLabel(def)}
                          </Typography>
                          {hasValue && (
                            <Chip
                              label={t('common:filters.clear', 'Effacer')}
                              size="small"
                              onClick={() => handleClearKey(singleKey)}
                              sx={clearBtnSx}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {opts.map((opt) => {
                            const selected = currentVals.includes(opt);
                            return (
                              <Chip
                                key={opt}
                                label={tOpt(opt)}
                                size="small"
                                onClick={() => {
                                  const next = selected
                                    ? currentVals.filter((v) => v !== opt)
                                    : [...currentVals, opt];
                                  if (next.length === 0) handleClearKey(singleKey);
                                  else handleChange({ [singleKey]: next });
                                }}
                                sx={activeChipSx(selected)}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}

                  {/* Select → single-value dropdown */}
                  {selectDefs.map((def) => {
                    const singleKey = `details_${def.key}`;
                    const opts = parseOpts(def);
                    const hasValue = !!value[singleKey];
                    return (
                      <Box key={def.key} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: hasValue ? 'primary.main' : undefined }}>
                            {tLabel(def)}
                          </InputLabel>
                          <Select
                            value={value[singleKey] || ''}
                            label={tLabel(def)}
                            onChange={(e) => {
                              if (!e.target.value) handleClearKey(singleKey);
                              else handleChange({ [singleKey]: e.target.value });
                            }}
                            sx={
                              hasValue
                                ? {
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'primary.main',
                                      borderWidth: 2
                                    }
                                  }
                                : undefined
                            }
                          >
                            <MenuItem value="">
                              <em>{t('common:search.details.any', 'Peu importe')}</em>
                            </MenuItem>
                            {opts.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {tOpt(opt)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Divider between groups */}
              {(rangeDefs.length > 0 || checkboxDefs.length > 0 || selectDefs.length > 0) &&
                toggleDefs.length > 0 && <Divider sx={{ my: 2 }} />}

              {/* ─── Toggle (boolean) fields ─────────────────────────────── */}
              {toggleDefs.length > 0 && (
                <Box>
                  <Typography sx={subHeadingSx}>
                    {t('common:search.details.features', 'Équipements & caractéristiques')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {toggleDefs.map((def) => {
                      const singleKey = `details_${def.key}`;
                      const isActive =
                        value[singleKey] === true || value[singleKey] === 'true';
                      return (
                        <Chip
                          key={def.key}
                          label={tLabel(def)}
                          size="small"
                          onClick={() => {
                            if (isActive) handleClearKey(singleKey);
                            else handleChange({ [singleKey]: true });
                          }}
                          sx={toggleChipSx(isActive)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ListingDetailsFilter;
